/**
 * The keys a playground config, definition, pair and pair side may carry — read at load, so a key the type does not
 * have (a typo, a key that belongs on another of them, one a newer version removed) fails loudly instead of being
 * silently ignored. Each list is checked against its type: `satisfies Record<keyof T, true>` makes the compiler refuse a
 * list that misses a key of the type or invents one, so the runtime list cannot drift from the type.
 */
import type { PlaygroundConfig, PlaygroundDefinition } from '../types.js';
import type { PlaygroundPairDefinition, PlaygroundPairSide } from './pair.js';

/** Every key of a `PlaygroundDefinition`. */
export const DEFINITION_KEYS = {
  title: true, description: true, environment: true, clientConfig: true, preload: true, entry: true, globals: true, stubs: true,
  mockConfig: true, assetRoots: true, allowRoots: true, dedupe: true, optimizeDeps: true, setup: true, gameHooks: true,
  tests: true, test: true, scenario: true, ready: true, readyTimeoutMs: true, game: true, videoBlackLimit: true,
} satisfies Record<keyof PlaygroundDefinition, true>;

/** Every key of a `PlaygroundConfig`. */
export const CONFIG_KEYS = {
  playgrounds: true, rootDir: true, port: true, host: true, autoOpen: true, preload: true, definitionPreload: true, stubs: true,
  optimizeDeps: true, assetRoots: true, allowRoots: true, dedupe: true, gameHooks: true, title: true, description: true,
  clientConfig: true, mockConfig: true, videoBlackLimit: true,
} satisfies Record<keyof PlaygroundConfig, true>;

/** Every key of a `PlaygroundPairDefinition` (the pair marker is a symbol, never a key). */
export const PAIR_KEYS = {
  title: true, description: true, readyTimeoutMs: true, clientConfig: true, mockConfig: true, preload: true, entry: true, globals: true,
  stubs: true, assetRoots: true, allowRoots: true, dedupe: true, optimizeDeps: true, gameHooks: true, videoBlackLimit: true, scenario: true,
  online: true, offline: true,
} satisfies Record<keyof PlaygroundPairDefinition, true>;

/** Every key of a `PlaygroundPairSide`. */
export const PAIR_SIDE_KEYS = {
  environment: true, setup: true, ready: true, game: true, clientConfig: true, mockConfig: true,
} satisfies Record<keyof PlaygroundPairSide, true>;

/**
 * What `validateKnownKeys` says about the keys `value` carries that `known` does not list — `Unknown key(s) "a", "b" in
 * <what>. Known keys: <list>.` — or `null` when it carries none. Only `value`'s own string keys are read — a symbol (the
 * pair marker) never trips it — and only `known`'s own keys are known: `constructor` is not a key of anything here.
 */
export const describeUnknownKeys = (value: object, known: Record<string, true>, what: string): string | null => {
  const unknown = Object.keys(value).filter((key) => !Object.hasOwn(known, key));
  if (!unknown.length) return null;
  return (
    `Unknown key${unknown.length > 1 ? 's' : ''} ${unknown.map((key) => JSON.stringify(key)).join(', ')} in ${what}. ` +
    `Known keys: ${Object.keys(known).join(', ')}.`
  );
};

/** Throws naming every key `value` carries that `known` does not list, `what` carried them, and the known keys (`describeUnknownKeys`). */
export const validateKnownKeys = (value: object, known: Record<string, true>, what: string): void => {
  const unknown = describeUnknownKeys(value, known, what);
  if (unknown) throw new Error(unknown);
};
